import { apiClient } from './client';
import { Role, Permission, UserRole, RoleAssignment } from '../../types/api';

export const rolesApi = {
  /**
   * Get all available permissions
   * @returns List of permissions
   */
  getPermissions: async (): Promise<Permission[]> => {
    const response = await apiClient.get<Permission[]>('/roles/permissions');
    return response.data;
  },

  /**
   * Get all available roles
   * @returns List of roles
   */
  getRoles: async (): Promise<Role[]> => {
    const response = await apiClient.get<Role[]>('/roles/');
    return response.data;
  },

  /**
   * Create a new custom role
   * @param data - Role creation data
   * @returns Created role
   */
  createRole: async (data: Partial<Role>): Promise<Role> => {
    const response = await apiClient.post<Role>('/roles/', data);
    return response.data;
  },

  /**
   * Get specific role by ID
   * @param roleId - Role ID
   * @returns Role
   */
  getRole: async (roleId: string): Promise<Role> => {
    const response = await apiClient.get<Role>(`/roles/${roleId}`);
    return response.data;
  },

  /**
   * Update specific role
   * @param roleId - Role ID
   * @param data - Role update data
   * @returns Updated role
   */
  updateRole: async (roleId: string, data: Partial<Role>): Promise<Role> => {
    const response = await apiClient.put<Role>(`/roles/${roleId}`, data);
    return response.data;
  },

  /**
   * Delete specific role
   * @param roleId - Role ID
   */
  deleteRole: async (roleId: string): Promise<void> => {
    await apiClient.delete(`/roles/${roleId}`);
  },

  /**
   * Assign role to user
   * @param assignment - Role assignment data
   * @returns User role assignment
   */
  assignRole: async (assignment: RoleAssignment): Promise<UserRole> => {
    const response = await apiClient.post<UserRole>('/roles/assign', assignment);
    return response.data;
  },

  /**
   * Revoke user role assignment
   * @param userRoleId - User role assignment ID
   */
  revokeRole: async (userRoleId: string): Promise<void> => {
    await apiClient.put(`/roles/revoke/${userRoleId}`);
  },

  /**
   * Get user's role assignments
   * @param userId - User ID
   * @returns List of user role assignments
   */
  getUserRoles: async (userId: string): Promise<UserRole[]> => {
    const response = await apiClient.get<UserRole[]>(`/roles/users/${userId}/roles`);
    return response.data;
  },
};
